import React, { useState, useEffect } from 'react';
import { FormControl, FormLabel, HStack, Textarea, Text, Spinner, Flex } from '@chakra-ui/react';
import CustomModal from '../common/CustomModal';
import Button from '../common/Button';
import CustomInput from '../common/Input';
import Rating from '../common/Rating';
import { useGetAdminReview } from '@/services/reviews';
// import useCustomToast from '@/components/common/Toast';
interface Vendor {
  id: number;
  label: string;
}

interface AddEditTestimonialModalProps {
  isOpen: boolean;
  onClose: () => void;
  editId: number | null;
  editTestimonial?: {
    rating: number;
    reviewer_name: string;
    content: string;
  };
  isEditTestimonialLoading?: boolean;
  vendors: Vendor[];
  ratingOptions: { id: string; label: string }[];
  onSubmit: (payload: { rating: string; reviewer_name: string; content: string }) => void;
  isLoading?: boolean;
}

const AddEditReviewModal: React.FC<AddEditTestimonialModalProps> = ({
  isOpen,
  onClose,
  editId,
  editTestimonial: propEditTestimonial,
  isEditTestimonialLoading: propIsEditTestimonialLoading,
  // vendors,
  // ratingOptions,
  onSubmit,
  isLoading,
}) => {
  const maxCommentLength = 500;
  // const { showToast } = useCustomToast();

  const [testimonialData, setTestimonialData] = useState<{
    rating: number;
    reviewer_name: string;
    content: string;
  }>({
    rating: 0,
    reviewer_name: '',
    content: '',
  });

  // Fetch testimonial data for editing
  const { data: fetchedTestimonial, isLoading: isFetchLoading } = useGetAdminReview(editId ?? 0, {
    enabled: editId !== null,
  });

  useEffect(() => {
    if (editId !== null && fetchedTestimonial && !isFetchLoading) {
      setTestimonialData({
        rating: Number(fetchedTestimonial.rating) || 0,
        reviewer_name: fetchedTestimonial.reviewer_name || '',
        content: fetchedTestimonial.content || '',
      });
    } else if (propEditTestimonial && !propIsEditTestimonialLoading) {
      setTestimonialData({
        rating: Number(propEditTestimonial.rating) || 0,
        reviewer_name: propEditTestimonial.reviewer_name || '',
        content: propEditTestimonial.content || '',
      });
    } else {
      setTestimonialData({
        rating: 0,
        reviewer_name: '',
        content: '',
      });
    }
  }, [
    fetchedTestimonial,
    isFetchLoading,
    propEditTestimonial,
    propIsEditTestimonialLoading,
    editId,
  ]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTestimonialData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= maxCommentLength) {
      handleInputChange(e);
    }
  };

  const handleSelectChange = (name: string, value: number) => {
    if (name === 'rating') {
      setTestimonialData((prev) => ({ ...prev, rating: value }));
    }
  };

  const handleSubmit = () => {
    const payload = {
      rating: testimonialData.rating.toString(),
      reviewer_name: testimonialData.reviewer_name,
      content: testimonialData.content,
    };
    onSubmit(payload);
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title={editId !== null ? 'Edit Testimonial' : 'Add Testimonial'}
      showCloseButton={true}
      size="2xl"
      footerContent={
        <HStack width="full" justifyContent="space-between">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isDisabled={
              !testimonialData.reviewer_name ||
              !testimonialData.content ||
              testimonialData.rating === 0
            }
            isLoading={isLoading || isFetchLoading}
          >
            {editId !== null ? 'Update' : 'Save'}
          </Button>
        </HStack>
      }
    >
      {isLoading || isFetchLoading || (editId !== null && propIsEditTestimonialLoading) ? (
        <Flex minHeight="300px" width="100%" align="center" justify="center">
          <Spinner size="md" color="brand.primary.DEFAULT" />
        </Flex>
      ) : (
        <>
          <FormControl mb={4}>
            <FormLabel fontSize="sm" color="text.secondary">
              Reviewer Name{' '}
              <Text as="span" color="brand.primary.DEFAULT">
                *
              </Text>
            </FormLabel>
            <CustomInput
              name="reviewer_name"
              value={testimonialData.reviewer_name || ''}
              onChange={handleInputChange}
              placeholder="Enter reviewer name"
              textColor="text.primary"
              fontWeight="medium"
            />
          </FormControl>
          <FormControl mb={4}>
            <FormLabel fontSize="sm" color="text.secondary">
              Rating{' '}
              <Text as="span" color="brand.primary.DEFAULT">
                *
              </Text>
            </FormLabel>
            <Rating
              initialRating={testimonialData.rating ?? 0}
              onChange={(rating) => handleSelectChange('rating', rating)}
              boxSize="5"
            />
          </FormControl>
          <FormControl mb={4}>
            <FormLabel fontSize="sm" color="text.secondary">
              Comment{' '}
              <Text as="span" color="brand.primary.DEFAULT">
                *
              </Text>
            </FormLabel>
            <Textarea
              name="content"
              value={testimonialData.content || ''}
              onChange={handleCommentChange}
              placeholder="Enter the short review"
              textColor="#090F1A"
              fontWeight={400}
              fontFamily="Josefin Sans"
              fontStyle="normal"
              fontSize="14px"
              lineHeight="20px"
              letterSpacing="0%"
              _hover={{ borderColor: 'gray.400' }}
              _focus={{
                borderColor: 'brand.primary.DEFAULT',
                borderWidth: '2px',
                boxShadow: '0 0 0 1px red.500',
              }}
              bg={'#FFFFFF'}
              maxLength={maxCommentLength}
            />
            <Text
              fontFamily="Josefin Sans"
              fontWeight={400}
              fontSize="12px"
              color="#4A5769"
              mt={2}
              textAlign="right"
            >
              {testimonialData.content?.length || 0}/{maxCommentLength} characters
            </Text>
          </FormControl>
        </>
      )}
    </CustomModal>
  );
};

export default AddEditReviewModal;
